import { jwtDecode } from 'jwt-decode';
import { useState } from 'react';
import PropTypes from 'prop-types';

import * as authenticationService from '@frolight/services/api/authenticationService';

import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';
import useAuth from '@frolight/hooks/useAuth';

import Button from '@frolight/components/Button';

const SignInForm = ({ onSignInSuccess }) => {
  const { lftext } = useLocalizedFormattedText();

  const { auth, setAuth } = useAuth();

  const [ loading, setLoading ] = useState(false);
  const [ email, setEmail ] = useState(null);
  const [ password, setPassword ] = useState(null);
  const [ error, setError ] = useState(null);
  
  const validateForm = () => {
    return email && email.length > 0 && password && password.length > 0;
  }

  const onSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const signInResponse = await authenticationService.signIn(new AbortController(), {
        username: email,
        password: password
      });

      if (!signInResponse.access_token) {
        throw new Error('No access token received');
      }

      const decodedToken = jwtDecode(signInResponse.access_token);
      setAuth({
        user: {
          id: decodedToken.sub,
          firstName: decodedToken.given_name,
          lastName: decodedToken.family_name,
          email: decodedToken.email,
          avatar: decodedToken.picture
        },
        memberships: decodedToken.memberships,
        roles: decodedToken.roles,
        accessToken: {
          token: signInResponse.access_token,
          type: signInResponse.token_type
        }
      });

      onSignInSuccess();
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  const renderError = () => {
    let showError = false;
    let errorMessage = '';

    if (error) {
      switch (error.errorCode) {
        case 404:
          showError = true;
          errorMessage = lftext('pages.sign_in.form.error_messages.email_not_found');
          break;
        case 401:
          showError = true;
          errorMessage = lftext('pages.sign_in.form.error_messages.wrong_password');
          break;
        case 500:
          showError = true;
          errorMessage = lftext('pages.sign_in.form.error_messages.something_wrong');
          break; 
        default:
          break;
      }
    }

    if (showError) {
      return (
        <div className="form__feedback">
          <p className="feedback_message  feedback_message--error">
            {errorMessage}
          </p>
        </div>
      );
    }
    return null;
  }

  return (
    <form className="form  form--sign_in" onSubmit={onSubmit} method="post">
      <div className="form__title">
        <h2>{lftext('pages.sign_in.form.title')}</h2>
      </div>
      { renderError() }
      <div className="form__field">
        <div className="text_input  text_input--email">
          <label className="text_input__label" htmlFor="sign-in-email">{lftext('pages.sign_in.form.email')}</label>
          <input
            id="sign-in-email"
            className="text_input__field"
            type="email"
            name="email"
            required
            autoFocus
            onChange={ (e) => setEmail(e.target.value) }
            disabled={ loading }
          />
        </div>
      </div>
      <div className="form__field">
        <div className="text_input  text_input--password">
          <label className="text_input__label" htmlFor="sign-in-password">{lftext('pages.sign_in.form.password')}</label>
          <input
            id="sign-in-password"
            className="text_input__field"
            type="password"
            name="password"
            required
            onChange={ (e) => setPassword(e.target.value) }
            disabled={ loading }
          />
        </div>
      </div>
      <div className="form__actions">
        <Button
          type="submit"
          className="button--full_width"
          label={lftext('pages.sign_in.form.submit')}
          priority="primary"
          disabled={ loading || !validateForm() }
        />
      </div>
    </form>
  );
}

SignInForm.propTypes = {
  onSignInSuccess: PropTypes.func.isRequired
};

export default SignInForm;
